import { Button, Empty, Skeleton } from 'antd'
import { useTranslation } from 'react-i18next'

import type { ChatFileListItem } from './attachments'
import { ChatFilesListRow } from './ChatFilesListRow'
import { useStyles } from './styles'
import { useAttachmentActions } from './useAttachmentActions'
import type { ResolveAttachmentUrl } from './useAttachmentActions'

type Props = {
  files: ChatFileListItem[]
  loading: boolean
  error?: boolean
  onRetry?: () => void
  resolveUrl?: ResolveAttachmentUrl
}

export const ChatFilesList = ({
  files,
  loading,
  error,
  onRetry,
  resolveUrl
}: Props) => {
  const { styles } = useStyles()
  const { t } = useTranslation()
  const { busyId, open } = useAttachmentActions(resolveUrl)

  if (loading) {
    return (
      <Skeleton
        active
        avatar={{ shape: 'square', size: 'small' }}
        title={false}
        paragraph={{ rows: 3 }}
      />
    )
  }

  if (error) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={t('workspaceHome.attachments.loadError')}
      >
        {onRetry ? (
          <Button size="small" onClick={onRetry}>
            {t('workspaceHome.attachments.retryLoad')}
          </Button>
        ) : null}
      </Empty>
    )
  }

  if (!files.length) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={t('workspaceHome.attachments.empty')}
      />
    )
  }

  return (
    <ul className={styles.filesList}>
      {files.map((file) => (
        <ChatFilesListRow
          key={file.id}
          file={file}
          busy={busyId === file.id}
          onOpen={open}
        />
      ))}
    </ul>
  )
}
